import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useViewEvent } from '@/clients/firebase/analytics'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import { NextSeo } from 'next-seo'
import { AppBanner } from '@/clients/components/organisms/Banner/AppBanner'
import { GlobalNav } from '@/clients/components/organisms/GlobalNav'
import { Footer } from '@/clients/components/molecules/Footer'
import { Users } from '@/clients/firebase/firestore/Users'
import Head from 'next/head'
import { Bosyu } from '@/clients/firebase/firestore/Bosyu'
import { BosyuDetail } from '@/clients/components/organisms/Bosyu/BosyuDetail'
import { getBosyu } from '@/clients/firebase/bosyu/getBosyu'
import { getBosyuUser } from '@/clients/firebase/bosyu/getBosyuUser'

type Props = {
  bosyu: Bosyu
  bosyuUser: Users | null
  hostUrl: string
}

const Component: React.FC<Props> = ({ bosyu, bosyuUser, hostUrl }) => {
  useViewEvent('BosyuDetailView')
  const router = useRouter()
  // bosyuUserがnullの場合は404ページにリダイレクト
  useEffect(() => {
    if (!bosyuUser) {
      router.replace('/404')
    }
  }, [bosyuUser, router])

  // bosyuUserがnullの場合はローディング表示
  if (!bosyuUser) {
    return <div>Loading...</div>
  }

  const ogpImageUrl = bosyu.ogpImageUrl
    ? bosyu.ogpImageUrl
    : `https://storage.googleapis.com/nadare-production-static-images/static/images/bosyus/tmp/img_tmp_${bosyu.bosyuGameTitle}.jpg`

  return (
    <>
      <NextSeo
        title={`${bosyuUser.name}さんの募集 | Gamee(ゲーミー)`}
        description={
          'ゲーム友達がすぐ見つかる話題のサイト！ApexLegends(エーペックスレジェンズ)やVALORANT(ヴァロラント)などのゲーム友達をランクやプレイスタイルで募集できるサイトです。フレンド募集をしている人は使ってみてね！'
        }
        openGraph={{
          url: hostUrl,
          title: `${bosyuUser.name}さんの募集 | Gamee(ゲーミー)`,

          images: [
            {
              url: ogpImageUrl,
              width: 1200,
              height: 630,
              alt: `${bosyuUser.name}さんの募集`,
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />
      <Head>
        <meta
          name="thumbnail"
          content={`https://gamee.games/static/images/img_app_install.jpg`}
        />
      </Head>
      <AppBanner />
      <GlobalNav />
      <BosyuDetail
        bosyu={bosyu}
        bosyuUser={bosyuUser}
      />
      <Footer />
    </>
  )
}

export default Component

export const getServerSideProps: GetServerSideProps<
  Props,
  { id: string }
> = async ({ params, req }: GetServerSidePropsContext<{ id: string }>) => {
  const bosyu = await getBosyu(params?.id || '')
  if (!bosyu) {
    return {
      notFound: true,
    }
  }
  const bosyuUser = await getBosyuUser(bosyu.userId)
  const hostUrl = req.headers.host
    ? `https://${req.headers.host}/bosyus/${params?.id}`
    : ''

  return {
    props: JSON.parse(JSON.stringify({
      bosyu,
      bosyuUser,
      hostUrl,
    })),
  }
}